// TODO: Reactivate sort functionality for V2
import React, { useEffect, useState } from 'react'
import Actions from '../../../../actions'
import { setUrlParams } from '../../../../utils/useParamUtil'
import {
  updateProvidersSearchFromUrl,
  countActiveFilters
} from 'utils/providerSearchUtils'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Pagination, Spin } from 'antd'
import ToggleButton from '../../../common/ToggleButton'
import Page from '../../../common/Page'
import $t from '../../../../i18n'
import AnimatedLoadingLabel from 'components/common/AnimatedLoadingLabel'
import { getUrlParams } from 'utils/useParamUtil'
import { directoryAccessParams } from 'utils/directoryAccessUtils'
import {
  INDIVIDUAL_PROVIDER_TYPE,
  MAX_IN_PAGE,
  DEFAULT_PAGE,
  routes
} from '../../../../consts'
import EmptyList from '../../../common/EmptyList'
import ContactSupport from '../../../common/ContactSupport/ContactSupport'
import ProviderFilterDialog from '../ProviderFilterDialog/ProviderFilterDialog'
import LocationSelector from '../LocationSelector'
import SearchBar from '../SearchBar'
import ProviderItem from '../ProviderItem'
import { isEmpty } from 'ramda'
import { event, eventTypes } from '../../../../consts/eventConsts'
import Alert from '../../../common/Alert'
import SearchBarDialog from '../SearchBarDialog/SearchBarDialog'
import { useNativeMessage } from 'hooks'
import { ProviderPCPTypes } from 'consts/ProviderConsts'
import AdditionalDataBanner from '../AdditionalDataBanner'
import './SearchProvidersResults.css'

export const SearchProvidersResults = () => {
  const dispatch = useDispatch()

  // Use native message hook to handle React Native WebView communication
  const { sendMessage, isWebView, isWebViewAuthenticated } = useNativeMessage({
    sendLoadedMessage: true,
    shouldSendLoaded: true
  })

  const [isFilterOptionsDisplay, setIsFilterOptionDisplay] = useState(false)
  const [sortBy, setSortBy] = useState(null)
  const [isTryPrintOverSizeLimit, setIsTryPrintOverSizeLimit] = useState(false)
  const [isMobileDialogOpen, setIsMobileDialogOpen] = useState(false)
  const [query, setQuery] = useState('')

  const {
    providersSearchLocation,
    providersSearchQuery,
    providersSearchResults: providers,
    isProvidersSearchLoading,
    providersSearchFilters,
    providersSearchCount,
    providersSupportedStates,
    providersDefaultState,
    providersSearchDisplayBanners
  } = useSelector(state => state.providers)

  const { isLoggedIn } = useSelector(state => state.auth)
  const isAuthenticated = isLoggedIn || isWebViewAuthenticated || isWebView

  const { members, selectedMember } = useSelector(state => state.account)
  const planDetailsCopay = useSelector(state => state?.nhMember?.memberCopay)
  const userDetails = useSelector(state => state.profile?.userDetails)
  const changePcpSelectedProviderId = useSelector(
    state => state.providers?.changePcpSelectedProviderId
  )
  const isFamily = !!members?.length

  const changePcpType =
    getUrlParams()?.changePcpType || ProviderPCPTypes.PHYSICAL

  const isChangePcpFlow = Boolean(
    isAuthenticated && getUrlParams()?.changePcp === 'true' && changePcpType
  )

  const searchState = providersSearchLocation?.state?.toUpperCase()
  const additionalDataBanner =
    searchState && providersSearchDisplayBanners?.[searchState]

  const onConfirmPcpSelection = provider => {
    // in case there is no previous physical pcp, update without popup approval
    const payload = {
      message: 'select-pcp',
      providerId: provider?.id,
      pcpType: changePcpType
    }
    // if we are in webview, we need to send a message to the native app
    if (isWebView) {
      sendMessage(payload)
    } else {
      // in case there is no previous physical pcp, update without popup approval
      onSelectPcpApproved(provider)
    }
  }

  const onSelectPcpApproved = provider => {
    const payload = {
      providerId: provider?.id,
      pcpType: changePcpType,
      patientId: selectedMember ? selectedMember?.id : userDetails?.id
    }
    dispatch(Actions.createAction(Actions.UPDATE_PCP_REQUESTED, payload))
  }

  const isStateOnlyLocation =
    providersSearchLocation?.state &&
    !providersSearchLocation?.city &&
    !providersSearchLocation?.zipcode

  const isSearchProvidersPdfRequested = useSelector(
    state => state.providers?.isSearchProvidersPdfRequested
  )

  const maxDistanceRadius = useSelector(
    state => state.providers?.distanceFilterDefinition?.max
  )

  const [searchParams, setSearchParams] = useState({
    page: 1,
    per_page: MAX_IN_PAGE
  })
  const [currentPageNumber, setCurrentNumber] = useState(1) // default page number is 1

  const handleSearch = query => {
    // Reset to page 1 for new search
    setCurrentNumber(DEFAULT_PAGE)

    // Update Redux state with new query
    dispatch(
      Actions.createAction(Actions.PROVIDERS_SEARCH_QUERY_UPDATED, query)
    )

    // Trigger search with immediate query override
    triggerSearch({ query, page: DEFAULT_PAGE })
  }

  useEffect(() => {
    dispatch(
      Actions.createAction(Actions.PROVIDERS_SEARCH_REQUESTED, {
        query: providersSearchQuery,
        providersSearchLocation,
        providersSearchFilters,
        sortBy,
        page: currentPageNumber,
        per_page: MAX_IN_PAGE
      })
    )
  }, [
    dispatch,
    providersSearchQuery,
    providersSearchLocation,
    providersSearchFilters,
    sortBy,
    currentPageNumber
  ])

  useEffect(() => {
    // Re-runs when the supported states arrive from the app config, so a deep link
    // to a state configured after this build still resolves to that state
    const queryParams = updateProvidersSearchFromUrl(
      dispatch,
      userDetails,
      providersSupportedStates,
      providersDefaultState
    )

    // Handle all URL parameters including location, query, filters, sorting, and pagination
    const searchParams = {
      ...queryParams,
      sortBy: queryParams?.sortBy ?? null,
      page: queryParams?.page ? parseInt(queryParams.page) : DEFAULT_PAGE,
      per_page: MAX_IN_PAGE
    }

    setSearchParams(searchParams)
    setSortBy(searchParams?.sortBy ?? null)
    setCurrentNumber(searchParams.page)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, providersSupportedStates, providersDefaultState])

  const onPageChange = pageNumber => {
    // Only change page number, don't reset
    setCurrentNumber(pageNumber)

    // Trigger search with new page
    triggerSearch()
  }

  const navigateToProviderPage = providerId => {
    // Preserve changePcp flow parameters from URL
    const currentUrlParams = getUrlParams()
    const changePcpParams = {}
    if (currentUrlParams.changePcp) {
      changePcpParams.changePcp = currentUrlParams.changePcp
    }
    if (currentUrlParams.changePcpType) {
      changePcpParams.changePcpType = currentUrlParams.changePcpType
    }

    const allParams = {
      query: providersSearchQuery,
      ...searchParams,
      ...providersSearchLocation,
      ...providersSearchFilters,
      ...changePcpParams,
      ...directoryAccessParams()
    }

    dispatch(
      Actions.createAction(
        Actions.NAVIGATED_TO,
        `${routes.PROVIDER_DETAILS.replace(':id', providerId)}?${setUrlParams(
          allParams
        )}`
      )
    )
  }

  const onNavigateBack = () => {
    const locationParams = {
      query: providersSearchQuery,
      ...{ year: providersSearchFilters?.year || null },
      ...providersSearchLocation
    }
    dispatch(
      Actions.createAction(
        Actions.NAVIGATED_TO,
        `${routes.SEARCH_PROVIDERS}?${setUrlParams(locationParams)}`
      )
    )
  }

  // Unified function to handle search updates - reads from state with overrides
  const triggerSearch = (overrides = {}, additionalActions = []) => {
    const searchData = {
      query: providersSearchQuery,
      providersSearchLocation,
      providersSearchFilters,
      sortBy,
      page: currentPageNumber,
      per_page: MAX_IN_PAGE,
      ...overrides
    }

    // Preserve changePcp flow parameters from URL
    const currentUrlParams = getUrlParams()
    const changePcpParams = {}
    if (currentUrlParams.changePcp) {
      changePcpParams.changePcp = currentUrlParams.changePcp
    }
    if (currentUrlParams.changePcpType) {
      changePcpParams.changePcpType = currentUrlParams.changePcpType
    }

    // Update URL with current state + overrides + preserved changePcp params.
    // The token rides along so the URL stays shareable and a reload keeps access
    const urlParams = {
      query: searchData.query,
      ...searchData.providersSearchLocation,
      ...searchData.providersSearchFilters,
      sortBy: searchData.sortBy,
      page: searchData.page,
      ...changePcpParams,
      ...directoryAccessParams()
    }

    window.history.replaceState(
      null,
      '',
      `${routes.SEARCH_PROVIDERS_RESULTS}?${setUrlParams(urlParams)}`
    )

    // Dispatch search action with current state + overrides
    dispatch(
      Actions.createAction(Actions.PROVIDERS_SEARCH_REQUESTED, searchData)
    )

    // Execute any additional actions
    additionalActions.forEach(action => dispatch(action))
  }

  const printResult = () => {
    if (providersSearchCount > 250) {
      setIsTryPrintOverSizeLimit(true)
    } else {
      printSearchResult()
    }
  }

  const printSearchResult = () => {
    dispatch(
      Actions.createAction(Actions.SEARCH_PROVIDERS_PDF_REQUESTED, {
        query: providersSearchQuery,
        providersSearchLocation,
        providersSearchFilters,
        sortBy
      })
    )
  }

  const printCloseAlert = () => {
    setIsTryPrintOverSizeLimit(false)
    printSearchResult()
  }

  // is the filter on - we check if one of the filter by params exists in the search params and its not empty
  // if true, the filter is on. else, not.
  const isFilterOn = Object.keys(searchParams)?.some(
    param =>
      Object.keys(providersSearchFilters || {}).includes(param) &&
      !!searchParams[param] &&
      !isEmpty(searchParams[param])
  )

  // count the number of active filters using utility function
  const activeFilterCount = countActiveFilters(providersSearchFilters)

  // gets the provider header - title + paging overview + sort and filter options
  const filterAndSort = () => {
    return (
      <div className='search-providers-results-filter-sort-container'>
        <div className='streach filter-button-container'>
          <ToggleButton
            className='filter-button action-button'
            checked={isFilterOn}
            checkOnClick={false}
            onChange={() => setIsFilterOptionDisplay(true)}
            iconClassName={'icon-filter-outline'}
            label={
              activeFilterCount > 0
                ? `${$t(
                    'providerPage.searchResults.filter.title'
                  )} (${activeFilterCount})`
                : $t('providerPage.searchResults.filter.title')
            }
            disabled={isChangePcpFlow}
          />
        </div>
        {/* <div className='sort-container' ref={sortOptionsRef}>
          <ToggleButton
            checked={sortBy}
            checkOnClick={false}
            className='sort-button action-button'
            iconClassName={'icon-sort-outline'}
            onChange={() => setIsSortOptionDisplay(!isSortOptionsDisplay)}
            label={
              <div className='sort-label'>
                {capitalizeFirstLetter(sortBy) ||
                  $t('providerPage.searchResults.sort.title')}
                <i className='icon-arrow-bold-down' />
              </div>
            }
          />
          {isSortOptionsDisplay && (
            <Card className='sort-options'>
              <li className='sort-option' onClick={() => handleSort('name')}>
                {$t('providerPage.searchResults.sort.name')}
              </li>
              <li
                className='sort-option'
                onClick={() => handleSort('distance')}
              >
                {$t('providerPage.searchResults.sort.distance')}
              </li>
              <li className='sort-option' onClick={() => handleSort('pcp')}>
                {$t('providerPage.searchResults.sort.pcp')}
              </li>
            </Card>
          )}
        </div> */}

        {!isChangePcpFlow && providers?.length > 0 && (
          <ToggleButton
            className='print-button action-button'
            checked={isSearchProvidersPdfRequested}
            checkOnClick={false}
            disabled={
              isChangePcpFlow ||
              isSearchProvidersPdfRequested ||
              (!isProvidersSearchLoading && providers?.length === 0)
            }
            onChange={() => printResult()}
            iconClassName={'icon-printer-outline'}
            label={
              <div className='print-label'>
                {isSearchProvidersPdfRequested && (
                  <div className='print-label-text'>
                    <AnimatedLoadingLabel dark={true} />
                  </div>
                )}
              </div>
            }
          />
        )}
      </div>
    )
  }

  const onChangePcpSelection = providerId => {
    let prviderIdToUpdate = providerId
    if (changePcpSelectedProviderId === providerId) {
      prviderIdToUpdate = null
    }

    dispatch(
      Actions.createAction(Actions.CHANGE_PCP_SELECTED_PROVIDER_ID, {
        providerId: prviderIdToUpdate
      })
    )
  }

  const onAdditionalActionClick = () => {
    // SOW
    if (isLoggedIn) {
      dispatch(Actions.createAction(Actions.NAVIGATED_TO, routes.GET_CARE))
    } else {
      dispatch(Actions.createAction(Actions.NAVIGATED_TO, routes.SIGN_IN))
    }

    // webview
    if (isWebView) {
      sendMessage('schedule-now')
    }
  }

  return (
    <Page
      className='search-provider-page search-providers provider-paging-container'
      title={$t('providerPage.searchResults.title')}
      fullWidth={true}
      onBack={isChangePcpFlow ? null : onNavigateBack}
    >
      <Alert
        title={$t('common.note')}
        className='redirect-alert'
        closable={true}
        shouldDisplay={isTryPrintOverSizeLimit}
        onCancel={() => {
          printCloseAlert()
        }}
        OkText={$t('common.ok')}
        onOk={() => {
          printCloseAlert()
        }}
      >
        {$t('providerPage.searchResults.printInSizeLimit')}
      </Alert>
      <div className='search-providers-results-header'>
        <LocationSelector />
        <SearchBar
          query={query}
          setQuery={setQuery}
          onSearch={handleSearch}
          isReadOnly={isChangePcpFlow}
          onClickSearchBar={isMobile =>
            isMobile ? setIsMobileDialogOpen(true) : null
          }
        />
        {isMobileDialogOpen && (
          <SearchBarDialog
            query={query}
            setQuery={setQuery}
            isVisible={isMobileDialogOpen}
            onClose={() => setIsMobileDialogOpen(false)}
            onSearch={handleSearch}
          />
        )}
      </div>
      {!isChangePcpFlow && filterAndSort()}
      <div className='search-results-subtitle'>
        {!!providersSearchCount
          ? $t('providerPage.searchResults.description', {
              from: (currentPageNumber - 1) * MAX_IN_PAGE + 1,
              to: Math.min(
                currentPageNumber * MAX_IN_PAGE,
                providersSearchCount
              ),
              count: providersSearchCount
            })
          : ''}
        <span className='search-results-subtitle-radius'>
          {!!providersSearchCount &&
          !!providersSearchFilters?.distanceRadius &&
          !isStateOnlyLocation
            ? ' '.concat(
                $t('providerPage.searchResults.radius', {
                  radius: providersSearchFilters?.distanceRadius
                })
              )
            : ''}
        </span>
      </div>
      {additionalDataBanner && (
        <AdditionalDataBanner
          title={$t(
            `providerPage.searchResults.additionalDataBanner.${searchState}.title`
          )}
          subText={$t(
            `providerPage.searchResults.additionalDataBanner.${searchState}.subText`
          )}
          link={additionalDataBanner.bannerLink}
          linkText={$t(
            `providerPage.searchResults.additionalDataBanner.${searchState}.linkText`
          )}
        />
      )}
      <div className='provider-list'>
        {isProvidersSearchLoading && <Spin className='loading' size='large' />}
        {!isProvidersSearchLoading &&
          !providers?.length &&
          (providersSearchFilters?.distanceRadius == maxDistanceRadius ? (
            <ContactSupport
              title={$t('providerPage.searchResults.empty_max_radius', {
                radius: providersSearchFilters?.distanceRadius || 100
              })}
            />
          ) : (
            <div className='empty-provider-list'>
              <EmptyList
                title={$t('providerPage.searchResults.empty_in_radius', {
                  radius: providersSearchFilters?.distanceRadius || 50
                })}
              />
              <Button
                type='primary'
                className='modify-distance-button'
                onClick={() => setIsFilterOptionDisplay(true)}
              >
                {$t('providerPage.searchResults.modifyDistance')}
              </Button>
            </div>
          ))}
        {!isProvidersSearchLoading &&
          !!providers?.length &&
          providers?.map(provider => (
            <ProviderItem
              key={provider.id}
              provider={provider}
              onClick={() => navigateToProviderPage(provider.id)}
              shouldShowDistance={
                !searchParams?.isNoLocation && !isStateOnlyLocation
              }
              onAdditionalActionClick={onAdditionalActionClick}
              hideNewMembersIndication={
                provider?.provider_type !== INDIVIDUAL_PROVIDER_TYPE
              }
              isLoggedIn={isAuthenticated}
              pcpFamilyOwnership={isFamily}
              isChangePcpFlow={isChangePcpFlow}
              onChangePcpSelection={onChangePcpSelection}
              onConfirmPcpSelection={onConfirmPcpSelection}
              changePcpSelectedProviderId={changePcpSelectedProviderId}
              planDetailsCopay={planDetailsCopay}
            />
          ))}
      </div>

      <div className='search-providers-paging-container'>
        <Pagination
          showSizeChanger={false}
          hideOnSinglePage={true}
          onChange={page => onPageChange(page)}
          current={currentPageNumber}
          defaultCurrent={currentPageNumber}
          pageSize={MAX_IN_PAGE}
          total={providersSearchCount}
        />
      </div>

      <ProviderFilterDialog
        isOpen={isFilterOptionsDisplay}
        defaultFilterBy={providersSearchFilters}
        onFilter={filter => {
          // Reset to page 1 for new filter
          setCurrentNumber(DEFAULT_PAGE)

          // Update Redux state with new filters
          dispatch(
            Actions.createAction(
              Actions.SEARCH_PROVIDER_FILTERS_UPDATED,
              filter
            )
          )

          // Trigger search with immediate filter override
          triggerSearch(
            { providersSearchFilters: filter, page: DEFAULT_PAGE },
            [
              Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
                type: eventTypes.formEvent,
                eventName: event.formEvent.submit,
                data: filter,
                element: 'Provider filter results'
              })
            ]
          )
        }}
        onClose={() => setIsFilterOptionDisplay(false)}
        disabled={isChangePcpFlow}
      />
    </Page>
  )
}

export default SearchProvidersResults
